import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as actions from './reportsActions';
import { openSnackbar } from '../_shared/snack-bar/snackbarActions';
import {
  selectReportsData,
  selectReportsLoading,
  selectExportLoading,
  selectReportsFilters,
  selectReportsTotalCount,
  selectDiscApprovalCount,
  selectFilteredProjects,
  selectBulkReportStatus,
} from './reportsSelectors';
import { initialFilterState } from './reportsReducer';
import ProjectFilters from './ProjectFilters';
import ProjectDetailsTab from './ProjectDetailsTab';
import CommentsTab from './CommentsTab';
import './Reports.scss';
import config from '../../styles/utils/config.scss';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: 'calc(100vh - 64px)',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    height: '100%',
  },
  summaryTitle: {
    fontSize: 14,
    color: '#666',
  },
  summaryValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: config.primaryColor,
  },
}));

const Reports = ({
  reportsData,
  loading,
  exportLoading,
  filters,
  totalCount,
  discApprovalCount,
  filteredProjects,
  bulkReportStatus,
  setFilter,
  resetFilter,
  setPage,
  fetchFilteredProjects,
  generateBulkReport,
  clearBulkReportStatus,
  showSnackbar,
}) => {
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleExportReport = (selectedColumns) => {
    // When user clicks Export to Excel
    // Use filters to export ALL filtered projects, not just current page
    if (!filters || Object.keys(filters).length === 0) {
      alert('Please apply filters first to see projects');
      return;
    }
    
    // Call bulk API to get detailed data for ALL filtered projects
    if (currentTab === 0) {
      // Project Details Report - Excel generated server-side
      generateBulkReport('projectDetails', filters, selectedColumns);
    } else {
      // Comments Report - Excel generated server-side
      generateBulkReport('commentsAnalysis', filters, null);
    }
  };

  const handleApplyFilters = () => {
    // Reset to first page when applying filters
    const filtersWithResetPage = { ...filters, page: 0 };
    setPage(0, filters.rowsPerPage);
    
    // Fetch filtered project list (basic info only) - same as Projects tab
    fetchFilteredProjects(filtersWithResetPage);
  };

  const handleResetFilter = () => {
    resetFilter();
    // Clear filtered projects
    fetchFilteredProjects(initialFilterState);
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    // Update page in state
    setPage(newPage, newRowsPerPage);
    
    // Fetch filtered project list for new page (basic info only)
    const updatedFilters = {
      ...filters,
      page: newPage,
      rowsPerPage: newRowsPerPage,
    };
    
    fetchFilteredProjects(updatedFilters);
  };

  const calculateSummary = () => {
    return {
      totalProjects: totalCount,
      discretionaryApproved: discApprovalCount,
    };
  };

  const summary = calculateSummary();

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" component="h1">
          System Reports
        </Typography>
      </div>

      {/* Filters */}
      <Paper className={classes.paper}>
        <ProjectFilters 
          filters={filters}
          setFilter={setFilter}
          resetFilter={handleResetFilter}
          onApplyFilters={handleApplyFilters}
          searchButtonText="Apply"
        />
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6}>
          <Card className={classes.summaryCard}>
            <CardContent>
              <Typography className={classes.summaryTitle}>
                Total Projects
              </Typography>
              <Typography className={classes.summaryValue}>
                {summary.totalProjects}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card className={classes.summaryCard}>
            <CardContent>
              <Typography className={classes.summaryTitle}>
                Discretionary Approved
              </Typography>
              <Typography className={classes.summaryValue}>
                {summary.discretionaryApproved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Tabs */}
      <Paper className={classes.paper}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Project Details Report" />
          <Tab label="Comments Report" />
        </Tabs>
        
        {currentTab === 0 && (
          <ProjectDetailsTab
            data={filteredProjects}
            reportData={reportsData}
            loading={loading}
            exportLoading={exportLoading}
            bulkReportStatus={bulkReportStatus}
            onExport={handleExportReport}
            filters={filters}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            clearBulkReportStatus={clearBulkReportStatus}
            showSnackbar={showSnackbar}
          />
        )}
        
        {currentTab === 1 && (
          <CommentsTab
            data={filteredProjects}
            reportData={reportsData}
            loading={loading}
            exportLoading={exportLoading}
            bulkReportStatus={bulkReportStatus}
            onExport={handleExportReport}
            filters={filters}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            clearBulkReportStatus={clearBulkReportStatus}
            showSnackbar={showSnackbar}
          />
        )}
      </Paper>

    </div>
  );
};

Reports.propTypes = {
  reportsData: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  exportLoading: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  discApprovalCount: PropTypes.number.isRequired,
  filteredProjects: PropTypes.array.isRequired,
  bulkReportStatus: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  resetFilter: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  fetchFilteredProjects: PropTypes.func.isRequired,
  generateBulkReport: PropTypes.func.isRequired,
  clearBulkReportStatus: PropTypes.func.isRequired,
  showSnackbar: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  reportsData: selectReportsData(state),
  loading: selectReportsLoading(state),
  exportLoading: selectExportLoading(state),
  filters: selectReportsFilters(state),
  totalCount: selectReportsTotalCount(state),
  discApprovalCount: selectDiscApprovalCount(state),
  filteredProjects: selectFilteredProjects(state),
  bulkReportStatus: selectBulkReportStatus(state),
});

const mapDispatchToProps = {
  setFilter: actions.setFilter,
  resetFilter: actions.resetFilter,
  setPage: actions.setPage,
  fetchFilteredProjects: actions.fetchFilteredProjects,
  generateBulkReport: actions.generateBulkReport,
  clearBulkReportStatus: actions.clearBulkReportStatus,
  showSnackbar: openSnackbar,
};

export default connect(mapStateToProps, mapDispatchToProps)(Reports);
